import { Injectable } from '@angular/core';
import { Config } from 'app/shared/services/config';
import { EMPTY, Observable, Subject } from 'rxjs';
import { LocationDetails, MonitorSeriesUI } from '../models/location-details';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ActivateTritonResponse, TritonLocation } from '../models/TritonLocation';
import { TritonConfiguration } from '../models/monitorsetting';
import { catchError, map, tap } from 'rxjs/operators';
import { EchoLocationPayload } from '../components/location-card/store/configs/echo/base-api-models';
import { TritonConfigurationPayload, TritonLocationPayload } from '../components/location-card/store/configs/triton/base-api-models';
import { RainAlertLocationPayload } from '../components/location-card/store/configs/rain-alert/base-api-models';
import { ForeSiteULLocationPayload } from '../components/location-card/store/configs/foresite-ul/base-api-models';
import { ForeSiteXDLocationPayload } from '../components/location-card/store/configs/foresite-xd/base-api-models';
import { FlexLocationPayload } from '../components/location-card/store/configs/flex/base-api-models';
import { GenericLocationPayload } from '../components/location-card/store/configs/generic/base-api-models';
import { EdgeDxLocationPayload } from '../components/location-card/store/configs/edge-dx/base-api-models';
import { UnknownLocationPayload } from '../components/location-card/store/configs/unknown/base-api-models';
import { TritonMonitorConfiguration } from '../components/location-card/store/configs/triton/models';
import { AddLocationRain } from '../models/add-location-rain';

export interface BasicInfoPayload {
    locationid?: number;
    locationName?: string;
    installationtypeid?: number;
    installationID?: number;
    ipaddress?: string;
    assignedRainGaugeId?: number;
    communicationType?: string;
    description?: string;
    installationDate?: string;
    monitorDeploymentID?: number;
    latitude?: number;
    longitude?: number;
    manholeaddress?: string;
    manholedepth?: number;
    qFinalEntityID?: number;
    serialnumber?: number;
    series?: string;
    status?: number;
    maintenanceInterval?: number;
    installationShapeType?: string;
    apnProvider?: string;
    samplerate?: number;
    fastrate?: number;
    usedaylightsaving?: boolean;
    synctime?: boolean;
    customProperties?: { key: string; value: string }[];
    pipe?: Record<string, unknown>;
    rain?: AddLocationRain;
    flume?: Record<string, unknown>;
    weir?: Record<string, unknown>;
    channel?: Record<string, unknown>;
    rainGauge?: Record<string, unknown>;
    composite?: Record<string, unknown>;
    elevationInstallation?: Record<string, unknown>;
    [key: string]: unknown;
}

export type LocationPayload =
  | EchoLocationPayload
  | TritonLocationPayload
  | RainAlertLocationPayload
  | ForeSiteULLocationPayload
  | ForeSiteXDLocationPayload
  | FlexLocationPayload
  | TritonLocation
  | GenericLocationPayload
  | EdgeDxLocationPayload
  | UnknownLocationPayload
  | BasicInfoPayload;

@Injectable()
export class TritonLocationService {
    public activationComplete$: Subject<void> = new Subject();

    constructor(
        private http: HttpClient
    ) {}

    public addLocation(customerId: number, addLocation: LocationPayload[] | LocationPayload) {
        const url = Config.urls.locationsCreate + '?customerId=' + customerId;

        const locations = Array.isArray(addLocation) ? addLocation : [addLocation];

        const res = this.http.post<number>(url, locations);
        res.pipe(
            catchError((error: HttpErrorResponse) => {
                console.error(error);
                return res;
            }),
        );
        return res;
    }

    public updateLocation(customerId: number, addLocation: LocationPayload[]) {
        const res = this.http.put<number>(Config.urls.locationsUpdate + '?customerId=' + customerId, addLocation);
        res.pipe(
            catchError((error: HttpErrorResponse) => {
                console.error(error);
                return res;
            }),
        );
        return res;
    }

    /**
     * get monitor-series api.
     */
    public getMonitorSeries(): Observable<Array<MonitorSeriesUI>> {
        return this.http.get<Array<MonitorSeriesUI>>(Config.urls.monitorSeries);
    }

    /**
     * Method returns location details for a particular location for a customer
     * @param customerID - customer ID
     * @param locationID - locationg ID
     */
    public getLocationDetails(customerID: number, locationID: number) {
        return this.http.get<LocationDetails>(`${Config.urls.locationDetails}/${customerID}/${locationID}`);
    }

    public tritonMonitorConfiguration(tritonConfiguration: TritonConfiguration | TritonConfigurationPayload, customerID: number) {
        return this.http.post<void>(Config.urls.configureTritonAlert + `${customerID}`, tritonConfiguration).pipe(
            map(
                (result) => {
                    return result;
                },
                () => {
                    return EMPTY;
                },
            ),
        );
    }

    public activateLocation(customerId: number, locationid: number) {
        return this.http
            .post<ActivateTritonResponse>(
                Config.serviceUrl +
                    Config.urls.activatedTritonMonitor +
                    `${customerId}&lid=${locationid}`,
                {},
            )
            .pipe(
                tap(() => this.activationComplete$.next(null))
            );
    }

    public getTritonMonitorConfiguration(locationId: number, customerID: number): Observable<TritonConfiguration | TritonMonitorConfiguration> {
        return this.http.get<TritonConfiguration | TritonMonitorConfiguration>(Config.urls.configureTritonAlert + `${customerID}` + `&lid=${locationId}`).pipe(
            map(
                (result) => {
                    return result;
                },
                () => {
                    return EMPTY;
                },
            ),
        );
    }

    public linkFlexLocationWithIoTHub(customerId: number, serialNumber: string, createFromPrism: boolean) {
        return this.http.post<number>(Config.urls.createFlex + `?customerId=${customerId}&deviceId=${serialNumber}&createFromPrism=${createFromPrism}`, {})
    }
}
